import { z } from 'zod';
import { ISTQBChapterRefSchema } from './ticket.schema';

export const QuizOptionSchema = z.object({
  id: z.string(),
  text: z.string(),
});

/**
 * Формат вопроса (checkpoint_exams · 25 JUN). По умолчанию `single` — обратная
 * совместимость со всеми существующими банками (поле отсутствует → один выбор).
 *   - single: радио, `answer` = id верной опции.
 *   - multi:  чекбоксы «выбери все верные», `answer` = id верных через запятую («a,c»).
 *   - text:   поле ввода короткого ответа, `options` пуст, `answer` = эталон + `accept` варианты.
 */
export const QuizQuestionTypeSchema = z.enum(['single', 'multi', 'text']);

export const QuizQuestionSchema = z.object({
  id: z.string(),
  bankId: z.string(),
  question: z.string(),
  options: z.array(QuizOptionSchema),
  answer: z.string(),
  /** Формат · отсутствует = 'single'. */
  type: QuizQuestionTypeSchema.optional(),
  /** Для type 'text' — дополнительные допустимые ответы (нормализованное сравнение). */
  accept: z.array(z.string()).optional(),
  /** Для type 'text' — подсказка в поле ввода. */
  placeholder: z.string().optional(),
  istqb: ISTQBChapterRefSchema.optional(),
  difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
  explanation: z.string(),
});

export const QuizBankKindSchema = z.enum([
  'sprint',
  'capstone-mock',
  'ctfl-mock-1',
  'ctfl-mock-2',
  'block1-exam',
  'block2-exam',
  'block3-exam',
]);

/** Per-question block-map entry · result breakdown (block label + ticket pointer). */
export const QuizBlockMapEntrySchema = z.object({
  block: z.string(),
  sourceTicket: z.string(),
});

export const QuizBankSchema = z.object({
  id: z.string(),
  sprintId: z.string().optional(),
  kind: QuizBankKindSchema,
  questions: z.array(QuizQuestionSchema),
  passThreshold: z.number().min(0).max(1),
  timeLimitSec: z.number().int().optional(),
  /**
   * slot_content_protection (29 JUN · quiz client cutover): result-review metadata
   * carried ON the bank so the gated `/api/quiz/banks/:id` delivers it WITHOUT the
   * client importing the heavy `quiz-banks*` modules (which hold every question +
   * answer). Both are server-attached from the static data; absent for banks that
   * don't need them.
   *   - blockMap:     per-question block label + source ticket (S07-S12 sprint banks).
   *   - courseTopics: per-question course-topic label (S02-S05 sprint banks + block exams).
   */
  blockMap: z.record(z.string(), QuizBlockMapEntrySchema).optional(),
  courseTopics: z.record(z.string(), z.string()).optional(),
});

export const QuizAttemptStateSchema = z.object({
  bankId: z.string(),
  answers: z.record(z.coerce.number().int(), z.string()),
  startedAt: z.string(),
  submittedAt: z.string().optional(),
  score: z.number().optional(),
  passed: z.boolean().optional(),
  attempts: z.number().int(),
});

export type QuizBlockMapEntry = z.infer<typeof QuizBlockMapEntrySchema>;
export type QuizOption = z.infer<typeof QuizOptionSchema>;
export type QuizQuestionType = z.infer<typeof QuizQuestionTypeSchema>;
export type QuizQuestion = z.infer<typeof QuizQuestionSchema>;
export type QuizBank = z.infer<typeof QuizBankSchema>;
export type QuizAttemptState = z.infer<typeof QuizAttemptStateSchema>;
